import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import { TableRow, TableCell, TableBody, Table, TableHead, Checkbox } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import Deed from 'entities/deed/model';
import { TableBox } from 'retired/Box';
import { purpleLighten80, white } from 'utils/color-styles';

import { addAttendeesAction } from './AddAttendeesOverlay/actions';
import { MoveToAttendeeListButton } from './MoveToAttendeeListButton';
import { WaitlistToAttendeeConfirmationModal } from './WaitlistToAttendeeConfirmationModal';
import { WaitlistEmptyContent } from './WaitlistEmptyContent';

interface Props {
  deed: Deed;
}

const columns = ['Name', 'Email'];

export const WaitlistTable = ({ deed }: Props) => {
  const dispatch = useDispatch();
  const [isSelectAllSelected, setIsSelectAllSelected] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const isEventWithShifts = deed.type === 'Event' && !!deed.roles?.size;
  const isWaitlistDisabled = deed.waitlistLimit === 0;

  const evenTypes = ['Event', 'BaseEvent'];

  if (!evenTypes.includes(deed.type) || isEventWithShifts || isWaitlistDisabled) {
    return null;
  }

  const handleMoveToAttendeeClick = () => {
    if (!selectedUsers.length) {
      return;
    }
    setShowConfirmationModal(true);
  };

  const handleAddAttendees = () => {
    if (!selectedUsers.length) {
      return;
    }
    dispatch(addAttendeesAction(deed.id, selectedUsers));
    setShowConfirmationModal(false);
    setSelectedUsers([]);
  };

  const handleCheckboxAllClick = () => {
    const isChecking = !isSelectAllSelected;
    setIsSelectAllSelected(isChecking);

    if (isChecking) {
      setSelectedUsers(deed.waitlist.map((waitlistee) => waitlistee._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleCheckboxItemClick = (userId: string) => {
    const isChecking = !selectedUsers.includes(userId);

    if (isChecking) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((user) => user !== userId));
    }
  };

  const isWaitlistEmpty = !deed.waitlist?.length;

  return (
    <>
      <TableBox style={{ marginTop: 20 }}>
        <Table size="small" className="colored purpleLighten80">
          <TableHead>
            <TableRow>
              <TableCell colSpan={columns.length + 1} style={{ padding: 0, border: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: 20,
                    paddingRight: 20,
                    paddingLeft: 20,
                    paddingBottom: isWaitlistEmpty ? 15 : 0,
                  }}
                >
                  <div
                    style={{
                      background: purpleLighten80,
                      color: white,
                      fontSize: '17px',
                    }}
                  >
                    Waitlist
                  </div>
                  {!isWaitlistEmpty && (
                    <MoveToAttendeeListButton onClick={handleMoveToAttendeeClick} />
                  )}
                </div>
              </TableCell>
            </TableRow>
            {!isWaitlistEmpty && (
              <TableRow>
                <TableCell size="small" style={{ background: purpleLighten80 }}>
                  <Checkbox
                    style={{ color: white }}
                    checked={isSelectAllSelected}
                    onChange={() => {
                      handleCheckboxAllClick();
                    }}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {isWaitlistEmpty && (
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <WaitlistEmptyContent />
                </TableCell>
              </TableRow>
            )}
            {deed.waitlist.map((waitlistee) => (
              <TableRow key={waitlistee.id}>
                <Checkbox
                  style={{ marginLeft: 15 }}
                  checked={selectedUsers.includes(waitlistee._id)}
                  onChange={() => {
                    handleCheckboxItemClick(waitlistee._id);
                  }}
                />
                <TableCell
                  style={{
                    width: '40%',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    lineHeight: '32px',
                  }}
                >
                  <Avatar
                    alt={waitlistee.name}
                    src={
                      waitlistee.mainPicture &&
                      waitlistee.mainPicture.replace('http://', 'https://')
                    }
                    style={{
                      width: 32,
                      height: 32,
                      float: 'left',
                      marginRight: 10,
                    }}
                  />
                  <Link to={`/volunteers/${waitlistee.id}`}>{waitlistee.name}</Link>
                </TableCell>
                <TableCell
                  style={{
                    width: '40%',
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {waitlistee.email || waitlistee.facebook?.email}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableBox>
      <WaitlistToAttendeeConfirmationModal
        showModal={showConfirmationModal}
        setShowModal={(show) => {
          setShowConfirmationModal(show);
        }}
        handleConfirm={handleAddAttendees}
        wouldBeOverbooked={selectedUsers.length + deed.attendees?.length > deed.attendeeLimit}
      />
    </>
  );
};
